/* eslint-disable no-param-reassign */
import { createRef, SerializableInstance } from '@funtech-int/rival';
import { assignDcbSubscriptionMutation } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { AuthManager } from '@funtech-int/yango-shared';

import { Logger } from '@kinopoisk-int/logger';

import { ProfileManager } from '@/shared/lib/profileManager';
import { GraphqlClient } from '@/shared/lib/graphqlClient';

interface LegacyDcbManager {
    needsSubscriptionPolling?: boolean;
    assign(): Promise<void>;
    startSubscriptionPolling(): Promise<void>;
    toJSON(): Record<string, any>;
}

interface LegacyDcbManagerData {
    needsSubscriptionPolling?: boolean;
}

interface LegacyDcbManagerParams {
    dataProvider: GraphqlClient;
    authManager: AuthManager;
    profileManager: ProfileManager;
    logger: Logger;
}

export const legacyDcbManagerRef = createRef<LegacyDcbManager>('legacyDcbManager');

interface PollingOptions {
    retries: number;
    interval: number;
    intervalCoefficient?: number;
}

async function startPolling<T>(
    fn: () => Promise<T>,
    { retries, interval, intervalCoefficient = 1 }: PollingOptions
): Promise<T> {
    try {
        return await fn();
    } catch (error) {
        if (retries - 1 <= 0) {
            throw error;
        }

        await new Promise((resolve) => {
            setTimeout(resolve, interval);
        });

        return startPolling(fn, {
            retries: retries - 1,
            interval: interval ** intervalCoefficient,
            intervalCoefficient,
        });
    }
}

export function createLegacyDcbManager({
    dataProvider,
    logger,
    authManager,
    profileManager,
}: LegacyDcbManagerParams): SerializableInstance<LegacyDcbManager, LegacyDcbManagerData> {
    let needsSubscriptionPolling: boolean | undefined;

    async function assign() {
        const { isAuthorized } = authManager;
        const hasSubscription = profileManager.hasSubscription.get();

        if (
            isAuthorized &&
            !hasSubscription &&
            typeof needsSubscriptionPolling === 'undefined' &&
            dataProvider
        ) {
            const response = await dataProvider?.fetch({
                operation: assignDcbSubscriptionMutation,
            });

            needsSubscriptionPolling = Boolean(
                response?.data?.data?.needsPollingForFeatureActivation
            );
        }
    }

    async function fetchUser() {
        await profileManager.updateSubscriptionInfo();
        if (!profileManager.hasSubscription.get()) {
            throw new Error('User has no subscription');
        }
    }

    async function startSubscriptionPolling() {
        if (!needsSubscriptionPolling) {
            return;
        }

        try {
            await startPolling(fetchUser, {
                retries: 10,
                interval: 1000,
                intervalCoefficient: 1.04,
            });
            needsSubscriptionPolling = false;
        } catch (e) {
            logger.error(
                { error: e },
                'Max retries of the request for subscription information after dcb assign exceeded'
            );
        }

        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    }

    return {
        get needsSubscriptionPolling() {
            return needsSubscriptionPolling;
        },
        assign,
        startSubscriptionPolling,

        ref: () => legacyDcbManagerRef,
        hydrate(data: LegacyDcbManagerData) {
            needsSubscriptionPolling = data.needsSubscriptionPolling;
        },
        toJSON: () => ({
            needsSubscriptionPolling,
        }),
    };
}
